import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CircularProgress, MenuItem, Select } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useBranchesAntiRating, useTripsAntiRating } from "hooks/useTicketsAntiRating";
import { useBranches } from "hooks/useBranches";
import {
  AntiRatingTypeFilter,
  BranchesAntiRatingItem,
  TripsAntiRatingItem,
} from "interfaces/entities/ticketStats";
import { buildTicketsHref } from "helpers/ticketsHref";

const COLOR_BAR = "#EF4444"; // антирейтинг → красный (плохо)

// Сгенирируй 100 цветов разные
const beautifulColors = [
  // ПАСТЕЛЬНЫЕ И НЕЖНЫЕ (Soft & Pastel)
  "#FFB7B2",
  "#FFDAC1",
  "#E2F0CB",
  "#B5EAD7",
  "#C7CEEA",
  "#F3E5F5",
  "#E1F5FE",
  "#E8F5E9",
  "#FFF9C4",
  "#FFE0B2",
  "#FCE4EC",
  "#F8BBD0",
  "#F06292",
  "#E1BEE7",
  "#D1C4E9",
  "#C5CAE9",
  "#BBDEFB",
  "#B3E5FC",
  "#B2EBF2",
  "#B2DFDB",

  // СОВРЕМЕННЫЙ МИНИМАЛИЗМ (Modern UI)
  "#4A90E2",
  "#50E3C2",
  "#B8E986",
  "#F5A623",
  "#D0021B",
  "#8B572A",
  "#9013FE",
  "#417505",
  "#BD10E0",
  "#4A4A4A",
  "#F8F9FA",
  "#E9ECEF",
  "#DEE2E6",
  "#CED4DA",
  "#ADB5BD",
  "#6C757D",
  "#495057",
  "#343A40",
  "#212529",
  "#007BFF",

  // МОРСКИЕ И ПРИРОДНЫЕ (Nature & Ocean)
  "#1A5276",
  "#1D8348",
  "#9A7D0A",
  "#922B21",
  "#76448A",
  "#1F618D",
  "#148F77",
  "#B7950B",
  "#AF601A",
  "#5D6D7E",
  "#008080",
  "#20B2AA",
  "#48D1CC",
  "#40E0D0",
  "#00CED1",
  "#5F9EA0",
  "#4682B4",
  "#6495ED",
  "#00BFFF",
  "#1E90FF",

  // ТЕПЛЫЕ И СОЛНЕЧНЫЕ (Warm & Sunset)
  "#FF5733",
  "#FF8D1A",
  "#FFC300",
  "#EDBB99",
  "#FAD7A0",
  "#F7DC6F",
  "#82E0AA",
  "#7DCEA0",
  "#85C1E9",
  "#BB8FCE",
  "#E67E22",
  "#D35400",
  "#C0392B",
  "#E74C3C",
  "#9B59B6",
  "#8E44AD",
  "#2980B9",
  "#3498DB",
  "#27AE60",
  "#2ECC71",

  // НЮДОВЫЕ И ПЫЛЬНЫЕ (Nude & Dusty)
  "#D4AC0D",
  "#A04000",
  "#2E4053",
  "#D5DBDB",
  "#AEB6BF",
  "#85929E",
  "#5D6D7E",
  "#34495E",
  "#17202A",
  "#FDFEFE",
  "#BC8F8F",
  "#CD5C5C",
  "#F08080",
  "#E9967A",
  "#FA8072",
  "#FFA07A",
  "#FF7F50",
  "#FF6347",
  "#FF4500",
  "#DC143C",
];

const buildYearOptions = (): number[] => {
  const current = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => current - i);
};

type ChartItem = {
  label: string; // подпись на оси Y
  secondary?: string; // мелкая подсказка для тултипа (код филиала, тур)
  count: number;
  percent: number;
  // ID для перехода в /admin/tickets с фильтром по этому филиалу/поездке.
  // null/undefined — переход недоступен (например, «Без филиала»).
  branchID?: string | null;
  groupTripID?: string | null;
};

type AntiRatingKind = "branches" | "trips";

interface AntiRatingChartProps {
  kind: AntiRatingKind;
  /** По умолчанию COMPLAINT. ALL — учитывать все обращения. */
  defaultType?: AntiRatingTypeFilter;
  /** Top-N. По умолчанию 10. */
  limit?: number;
  title?: string;
  branchID?: number | string;
}

const HUMAN_TYPE_LABEL: Record<AntiRatingTypeFilter, string> = {
  ALL: "обращений",
  COMPLAINT: "жалоб",
  SUGGESTION: "предложений",
  THANKS: "благодарностей",
  OTHER: "прочих обращений",
};

export const AntiRatingChart = ({
  kind,
  defaultType = "COMPLAINT",
  limit = 10,
  title,
  branchID,
}: AntiRatingChartProps) => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [type] = useState<AntiRatingTypeFilter>(defaultType);

  const [localBranchID, setLocalBranchID] = useState<number | "">("");
  const { data: branches } = useBranches();
  const activeBranchID = kind === "trips" && localBranchID !== "" ? localBranchID : branchID;

  const branchesQuery = useBranchesAntiRating(
    { year, type, limit, ...(activeBranchID ? { branchID: activeBranchID } : {}) },
    { enabled: kind === "branches" },
  );
  const tripsQuery = useTripsAntiRating(
    { year, type, limit, ...(activeBranchID ? { branchID: activeBranchID } : {}) },
    { enabled: kind === "trips" },
  );

  const { data, isLoading, error } = kind === "branches" ? branchesQuery : tripsQuery;

  const items: ChartItem[] = useMemo(() => {
    if (!data) return [];
    if (kind === "branches") {
      return (data.items as BranchesAntiRatingItem[]).map((it) => ({
        label: it.name,
        secondary: it.code ? `Код: ${it.code}` : undefined,
        count: it.count,
        percent: it.percent,
        branchID: it.branchID,
      }));
    }
    return (data.items as TripsAntiRatingItem[]).map((it) => ({
      label: it.name,
      secondary: it.tourPackageName || undefined,
      count: it.count,
      percent: it.percent,
      groupTripID: it.groupTripID,
    }));
  }, [data, kind]);

  const navigate = useNavigate();
  const goToTickets = (item: ChartItem) => {
    const typeFilter = type === "ALL" ? undefined : type;
    const from = `${year}-01-01`;
    const to = `${year}-12-31`;
    if (kind === "branches") {
      if (!item.branchID) return; // «Без филиала» — переходить некуда
      navigate(
        buildTicketsHref({
          branchID: item.branchID,
          type: typeFilter,
          period: "custom",
          from,
          to,
        }),
      );
      return;
    }
    if (!item.groupTripID) return;
    // В нашей модели Ticket.data.trainNumber хранит ID групповой поездки —
    // тот же паттерн используется в getTripsAntiRating на бэке.
    navigate(
      buildTicketsHref({
        trainNumber: item.groupTripID,
        type: typeFilter,
        period: "custom",
        from,
        to,
        branchID: activeBranchID ? String(activeBranchID) : undefined,
      }),
    );
  };

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<any>(null);

  const headerTitle =
    title ??
    (kind === "branches"
      ? "Антирейтинг филиалов по обращениям"
      : "Антирейтинг поездов по обращениям");

  const typeLabel = HUMAN_TYPE_LABEL[type];

  useEffect(() => {
    const ChartCtor = (window as any).Chart;
    if (!canvasRef.current || !ChartCtor) return;
    if (!items.length) {
      // если предыдущий чарт был — снести, чтобы не оставался устаревший
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    chartRef.current = new ChartCtor(canvasRef.current, {
      type: "bar",
      data: {
        labels: items.map((i) => i.label),
        datasets: [
          {
            label: typeLabel,
            data: items.map((i) => i.count),
            backgroundColor: beautifulColors.slice(0, items.length),
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      },
      options: {
        indexAxis: "y" as const,
        responsive: true,
        maintainAspectRatio: false,
        onHover: (_evt: any, els: any[], chart: any) => {
          // курсор-pointer на барах с доступным переходом
          const idx = els?.[0]?.index;
          const item = idx != null ? items[idx] : undefined;
          const navigable = !!(item && (item.branchID || item.groupTripID));
          chart.canvas.style.cursor = navigable ? "pointer" : "default";
        },
        onClick: (_evt: any, els: any[]) => {
          const idx = els?.[0]?.index;
          const item = idx != null ? items[idx] : undefined;
          if (item) goToTickets(item);
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (ctx: any[]) => {
                if (!ctx?.length) return "";
                const item = items[ctx[0].dataIndex];
                return item?.label || "";
              },
              label: (ctx: any) => {
                const item = items[ctx.dataIndex];
                if (!item) return "";
                const lines = [` ${item.count} ${typeLabel} (${item.percent}%)`];
                if (item.secondary) lines.push(` ${item.secondary}`);
                lines.push(" Кликните, чтобы открыть обращения");
                return lines;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { precision: 0, stepSize: 1 },
            grid: { display: false },
          },
          y: {
            ticks: { autoSkip: false },
            grid: { display: false },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [items, typeLabel]);

  // Высота под количество строк (минимум 320px)
  const chartHeight = Math.max(320, items.length * 36 + 40);

  return (
    <Card>
      <CardContent>
        <div className="flex flex-space-between items-center mb-2">
          <div>
            <h4 className="text-md font-bold mb-1">{headerTitle}</h4>
            {data && (
              <div className="text-sm color-gray">
                Всего {typeLabel}: {data.totalCount}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {kind === "trips" && (
              <Select
                size="small"
                value={localBranchID}
                onChange={(e) => setLocalBranchID(e.target.value as number | "")}
                displayEmpty
                style={{ minWidth: 160 }}
              >
                <MenuItem value="">Все филиалы</MenuItem>
                {branches?.data?.map((b: any) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            <Select size="small" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {buildYearOptions().map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {isLoading && (
          <div
            className="text-center"
            style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={28} />
          </div>
        )}

        {!isLoading && error && (
          <div
            className="text-center"
            style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Не удалось загрузить статистику
          </div>
        )}

        {!isLoading && !error && data && data.totalCount === 0 && (
          <div
            className="text-center color-gray"
            style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            За выбранный период нет {typeLabel}
          </div>
        )}

        {!isLoading && !error && items.length > 0 && (
          <div style={{ height: chartHeight, position: "relative" }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AntiRatingChart;
